import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Upload, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = axios.create({ withCredentials: true });

/**
 * Admin action: submit a stuck draft on the entrant's behalf.
 *
 * Fires the same submission-confirmation + payment-receipt emails the
 * participant would have received on the happy path, so the entrant ends up
 * in the identical state. Only shown for entries whose status is not yet
 * "submitted + paid".
 */
const AdminSubmitOnBehalfModal = ({ nomination, onClose, onSubmitted }) => {
  const invoiceRef = useRef(null);
  const [mode, setMode] = useState('paid'); // 'paid' | 'offline'
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [invoiceUrl, setInvoiceUrl] = useState('');
  const [invoiceName, setInvoiceName] = useState('');
  const [uploadPct, setUploadPct] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const uploadInvoice = async (file) => {
    if (!file) return;
    setUploading(true);
    setUploadPct(0);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await ax.post(`${API}/admin/uploads`, fd, {
        timeout: 180000,
        onUploadProgress: (evt) => {
          if (evt && evt.total) setUploadPct(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      setInvoiceUrl(r.data.url);
      setInvoiceName(r.data.filename);
      toast.success('Invoice attached.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invoice upload failed.');
    } finally {
      setUploading(false);
      setUploadPct(0);
    }
  };

  const submit = async () => {
    if (!reference.trim()) { toast.error('Payment reference is required.'); return; }
    setSubmitting(true);
    try {
      await ax.post(`${API}/admin/nominations/${nomination.id}/submit-on-behalf`, {
        payment_mode: mode,
        reference: reference.trim(),
        note: note.trim(),
        invoice_url: invoiceUrl || undefined,
        invoice_name: invoiceName || undefined,
      });
      toast.success('Entry submitted. Confirmation emails sent to the entrant.');
      if (typeof onSubmitted === 'function') onSubmitted();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Submission failed. Please retry.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="admin-submit-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-[var(--bg-alt)] border border-[var(--gold-faint)] max-w-lg w-full max-h-[88vh] overflow-y-auto">
        <div className="flex items-start justify-between p-6 border-b border-[var(--gold-faint)]">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--gold)]">Submit Entry on Behalf</div>
            <h3 className="font-[Libre_Baskerville] text-xl text-[var(--ink)] mt-1">{nomination.entry_title || 'Untitled'}</h3>
            <div className="text-[var(--ink-muted)] text-xs mt-1">
              {nomination.brand_name && <span>{nomination.brand_name} · </span>}
              #{nomination.entry_no || '—'} · {nomination.email || 'no email'}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-[var(--ink-muted)] hover:text-[var(--gold)] transition-colors p-1"
            aria-label="Close"
            data-testid="admin-submit-close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="border border-[var(--gold-faint)]/60 bg-[var(--gold)]/5 p-3 text-[12.5px] leading-relaxed text-[var(--ink)]/85">
            The entrant will be marked as submitted and paid, and will receive
            the standard submission confirmation and payment receipt emails.
            This action can’t be undone from the panel.
          </div>

          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--ink-muted)] mb-2">Payment Mode</div>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setMode('paid')}
                className={`border px-3 py-2.5 text-sm transition-colors ${mode === 'paid' ? 'border-[var(--gold)] text-[var(--gold)]' : 'border-[var(--gold-faint)] text-[var(--ink-muted)] hover:border-[var(--gold-soft)]'}`}
                data-testid="admin-submit-mode-paid"
              >
                {mode === 'paid' && <CheckCircle2 size={12} className="inline mr-1.5 -mt-0.5" />}
                Paid (Online / Razorpay)
              </button>
              <button
                type="button"
                onClick={() => setMode('offline')}
                className={`border px-3 py-2.5 text-sm transition-colors ${mode === 'offline' ? 'border-[var(--gold)] text-[var(--gold)]' : 'border-[var(--gold-faint)] text-[var(--ink-muted)] hover:border-[var(--gold-soft)]'}`}
                data-testid="admin-submit-mode-offline"
              >
                {mode === 'offline' && <CheckCircle2 size={12} className="inline mr-1.5 -mt-0.5" />}
                Offline Payment
              </button>
            </div>
          </div>

          <div>
            <label className="block text-[10px] tracking-[0.3em] uppercase text-[var(--ink-muted)] mb-2">
              Payment Reference / Transaction ID <span className="text-[var(--terracotta)]">*</span>
            </label>
            <input
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder={mode === 'paid' ? 'pay_XxxxxxxxxxxxXX' : 'UPI/NEFT reference number'}
              className="w-full bg-[var(--bg)] border border-[var(--gold-faint)] focus:border-[var(--gold)] outline-none p-2.5 text-[var(--ink)] text-sm"
              data-testid="admin-submit-reference"
            />
          </div>

          <div>
            <label className="block text-[10px] tracking-[0.3em] uppercase text-[var(--ink-muted)] mb-2">Internal Note (optional)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="Why is admin submitting? e.g. payment succeeded on Razorpay dashboard but callback failed."
              className="w-full bg-[var(--bg)] border border-[var(--gold-faint)] focus:border-[var(--gold)] outline-none p-2.5 text-[var(--ink)] text-sm resize-y"
              data-testid="admin-submit-note"
            />
          </div>

          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--ink-muted)] mb-2">Invoice (optional)</div>
            {invoiceUrl ? (
              <div className="flex items-center gap-3 border border-[var(--gold-faint)] bg-[var(--bg)] px-3 py-2.5 text-sm">
                <CheckCircle2 size={14} className="text-[var(--gold)] shrink-0" />
                <span className="truncate flex-1 text-[var(--ink)]">{invoiceName || 'Invoice attached'}</span>
                <button
                  type="button"
                  onClick={() => { setInvoiceUrl(''); setInvoiceName(''); }}
                  className="text-[var(--ink-muted)] hover:text-[var(--terracotta)]"
                  data-testid="admin-submit-invoice-clear"
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <>
                <input
                  ref={invoiceRef}
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/*"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadInvoice(f); e.target.value = ''; }}
                  data-testid="admin-submit-invoice-input"
                />
                <button
                  type="button"
                  disabled={uploading}
                  onClick={() => invoiceRef.current?.click()}
                  className="w-full inline-flex items-center justify-center gap-2 border border-dashed border-[var(--gold-faint)] hover:border-[var(--gold)] px-3 py-2.5 text-[var(--ink-muted)] hover:text-[var(--gold)] text-sm transition-colors disabled:opacity-50"
                  data-testid="admin-submit-invoice-upload"
                >
                  <Upload size={13} />
                  {uploading ? (uploadPct > 0 ? `Uploading ${uploadPct}%` : 'Uploading…') : 'Choose invoice (PDF or image)'}
                </button>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-[var(--gold-faint)]">
          <button
            type="button"
            onClick={onClose}
            className="text-[var(--ink-muted)] text-[10px] tracking-[0.3em] uppercase px-3 py-2 hover:text-[var(--ink)] transition-colors"
            data-testid="admin-submit-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting || uploading || !reference.trim()}
            className="bg-[var(--gold)] hover:bg-[var(--gold-bright)] text-[var(--bg)] text-[10px] tracking-[0.3em] uppercase px-5 py-2.5 transition-colors disabled:opacity-50"
            data-testid="admin-submit-confirm"
          >
            {submitting ? 'Submitting…' : 'Submit Entry'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminSubmitOnBehalfModal;
